/**
 * Frontend shadow-read reporting (test-integration plan Phase 1c).
 *
 * Off by default (REACT_APP_RDS_SHADOW_READ_SCHEDULES). Fire-and-forget
 * -- the caller never awaits this, so it can never add latency or
 * affect the real UI. Every branch is caught so it can never throw
 * into the caller. Reports only the OUTCOME to the backend (never row
 * content) via POST /api/rds-shadow-report/, which feeds the same
 * admin dashboard as the backend-side pilots (patient_labs, notes_c).
 *
 * Uses rdsPhi.getRow() for the single-row pilot. RDS authorizes that
 * read too: a viewer who is not entitled to the row gets the MASKED
 * projection with access_level "none", which is reported as "masked",
 * never as a mismatch.
 */
import { getSupabase } from "../components/supabaseClient";
import { getBackendURL } from "../components/env";
import {
  getRow, listRows, PhiClientError, PhiClientUnavailable,
} from "./rdsPhi";
import { isCutoverEffective } from "./rdsCutover";

const SCHEDULE_LIST_SAMPLE_SIZE = 20;
// The list compare is never awaited by anyone, so nothing else bounds it: a
// hung RDS must not hold one of the browser's six connections to it.
const SHADOW_LIST_TIMEOUT_MS = 8000;

function shadowReadEnabled() {
  const v = String(process.env.REACT_APP_RDS_SHADOW_READ_SCHEDULES || "").toLowerCase();
  return v === "1" || v === "true" || v === "yes";
}

function rowsRoughlyMatch(rdsRow, supabaseRow) {
  if (!rdsRow || !supabaseRow) return false;
  const commonKeys = Object.keys(rdsRow).filter(
    (k) => k !== "access_level" && Object.prototype.hasOwnProperty.call(supabaseRow, k),
  );
  return commonKeys.every(
    (k) => JSON.stringify(rdsRow[k]) === JSON.stringify(supabaseRow[k]),
  );
}

async function reportOutcome(pilot, fields) {
  try {
    const supabase = await getSupabase();
    const { data: { session } = {} } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) return;
    await fetch(`${getBackendURL()}/api/rds-shadow-report/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ pilot, ...fields }),
    });
  } catch (_) {
    // Reporting failures must never surface anywhere -- this is purely
    // diagnostic telemetry for the admin dashboard.
  }
}

/**
 * Fire-and-forget: compare `supabaseRow` (already fetched, already
 * being shown to the user) against RDS's copy of the same row, and
 * report the outcome. Never awaited by the caller, never throws.
 */
export function shadowCompareScheduleRow(schema, table, rowId, supabaseRow) {
  if (!shadowReadEnabled()) return;
  if (!schema || !table || !rowId) return;

  getRow(schema, table, rowId)
    .then((rdsRow) => {
      if (rdsRow?.access_level === "none") {
        reportOutcome("schedules_notes_c", {
          tenant: schema, row_id: String(rowId), outcome: "masked",
        });
        return;
      }
      const match = rowsRoughlyMatch(rdsRow, supabaseRow);
      reportOutcome("schedules_notes_c", {
        tenant: schema,
        row_id: String(rowId),
        outcome: match ? "match" : "mismatch",
      });
    })
    .catch((err) => {
      if (err instanceof PhiClientError) {
        reportOutcome("schedules_notes_c", {
          tenant: schema,
          row_id: String(rowId),
          outcome: "rds_error",
          status_code: err.statusCode,
          detail: err.detail,
        });
      } else if (err instanceof PhiClientUnavailable) {
        reportOutcome("schedules_notes_c", {
          tenant: schema, row_id: String(rowId), outcome: "rds_unavailable",
        });
      } else {
        reportOutcome("schedules_notes_c", {
          tenant: schema, row_id: String(rowId), outcome: "unexpected_error",
        });
      }
    });
}

/**
 * Fire-and-forget: compare a bounded sample of an already-fetched
 * list against RDS, batched per schema. Generic across tables/pilots
 * -- shadowCompareScheduleList and shadowComparePatientList below are
 * thin, named wrappers over this.
 *
 * Authorization is ALWAYS RDS's real rds_access.resolve(), computed
 * from each row's OWN user_id / shared_users plus the viewer's tenant
 * role -- the browser sends only ids (no owners map, no include_ids
 * bypass; see TEST_INTEGRATION_PLAN.md §11). A masked row is therefore
 * a real signal: RDS decided this viewer is not entitled to it.
 *
 * Parity semantics: Supabase is the source of truth for WHAT an
 * entitled row contains (matched / mismatched), not for WHO may see it
 * -- its RLS currently returns every row to every authenticated user.
 * `masked` is reported separately and is expected to be non-zero for
 * non-clinician viewers.
 *
 * @param {string} pilot name reported to /api/rds-shadow-report/
 *   (must be in that endpoint's _ALLOWED_CLIENT_PILOTS safelist).
 * @param {string} table RDS table name, e.g. "notes-c".
 * @param {Record<string, object[]>} rowsBySchema already-fetched
 *   Supabase rows, keyed by the schema they came from (handles both
 *   the single-schema case and a cross-schema provider fan-out).
 * @param {string} [overlayFlag] the PHI overlay flag those rows were read
 *   under. While it is effective the overlay (rdsPhiOverlay.js) already
 *   reports matched / mismatched / masked for every row of every such
 *   read, from its own audited /list, so this sampled second /list is
 *   skipped: it would only repeat that comparison and audit the rows
 *   again.
 */
async function shadowCompareList(pilot, table, rowsBySchema, overlayFlag) {
  if (!shadowReadEnabled()) return;
  if (!rowsBySchema) return;
  if (overlayFlag && (await isCutoverEffective(overlayFlag))) return;

  Object.entries(rowsBySchema).forEach(([schema, rows]) => {
    if (!schema || !rows || !rows.length) return;
    const sample = rows.slice(0, SCHEDULE_LIST_SAMPLE_SIZE);
    const byId = {};
    sample.forEach((r) => { if (r?.id) byId[String(r.id)] = r; });
    const ids = Object.keys(byId);
    if (!ids.length) return;

    listRows(schema, table, ids, { timeoutMs: SHADOW_LIST_TIMEOUT_MS })
      .then((rdsRows) => {
        const rdsById = {};
        rdsRows.forEach((r) => { if (r?.id) rdsById[String(r.id)] = r; });
        let matched = 0;
        let mismatched = 0;
        let missing = 0;
        let masked = 0;
        const columnsSeen = new Set();
        ids.forEach((id) => {
          const rdsRow = rdsById[id];
          if (!rdsRow) { missing += 1; return; }
          if (rdsRow.access_level === "none") {
            // Expected, not a bug: no owner/share/admin match was
            // found for this row -- exactly what a real cutover would
            // also return, so this must NOT be counted as a mismatch.
            masked += 1;
            return;
          }
          Object.keys(rdsRow).forEach((k) => {
            if (k !== "access_level") columnsSeen.add(k);
          });
          if (rowsRoughlyMatch(rdsRow, byId[id])) matched += 1;
          else mismatched += 1;
        });
        reportOutcome(pilot, {
          tenant: schema,
          table,
          sample_size: ids.length,
          matched,
          mismatched,
          missing,
          masked,
          columns: Array.from(columnsSeen).sort(),
          outcome: "ok",
        });
      })
      .catch((err) => {
        if (err instanceof PhiClientError) {
          reportOutcome(pilot, {
            tenant: schema, table, sample_size: ids.length,
            outcome: "rds_error", status_code: err.statusCode, detail: err.detail,
          });
        } else if (err instanceof PhiClientUnavailable) {
          reportOutcome(pilot, {
            tenant: schema, table, sample_size: ids.length, outcome: "rds_unavailable",
          });
        } else {
          reportOutcome(pilot, {
            tenant: schema, table, sample_size: ids.length, outcome: "unexpected_error",
          });
        }
      });
  });
}

/**
 * SchedulesTable.js's main day-list query (notes-c). The real,
 * frequently-hit "Schedules.js patient list" pilot the original plan
 * meant -- not the rare backup-restore-only refreshSelectedPatientFromDb
 * above. RDS resolves each row from its own user_id / shared_users
 * (mirrored from Supabase) and the viewer's tenant role -- the real
 * authorization path, no include_ids bypass. Skipped while
 * overlay_notes_c is effective (see shadowCompareList's overlayFlag).
 */
export function shadowCompareScheduleList(rowsBySchema) {
  shadowCompareList("schedules_list", "notes-c", rowsBySchema, "overlay_notes_c").catch(() => {});
}

/**
 * PatientListPage.js's roster query (patients_with_ehrid). Fires on
 * every normal visit to /patients. Roster rows have no owner in any
 * tenant, so RDS resolves them on the viewer's tenant role and the
 * rds_access.table_access policy for the roster (approved members ->
 * view); this is the actual access shape a real cutover would produce,
 * not a shadow-only simplification.
 */
export function shadowComparePatientList(rowsBySchema) {
  shadowCompareList("patient_list", "patients_with_ehrid", rowsBySchema).catch(() => {});
}
